import useGetLocationHookFactory from '@hooks/use-get-location-hook-factory';
import { debounce } from '@utils/debounce';
import { isFullPostcode } from '@utils/postcode-validation';
import ProgressLoader from 'components/Loader/ProgressLoader';
import { ErrorMessage, ErrorWrapper } from 'components/Search/styles';
import { useConsumerActions } from 'lib/consumer/consumer.hooks';
import { IconMyLocation } from 'lib/design-system';
import { PrimaryButton, SecondaryButton } from 'lib/mortar';
import {
  DropdownSearch,
  type DropdownOption,
} from 'lib/mortar/components/drop-down-search/drop-down-search';
import { track } from 'lib/sdks/analytics';
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import {
  FunnellingBackButton,
  FunnellingFooter,
  Header,
  Subtitle,
  Wrapper,
} from '../styles';
import type { ScreenCommonProps } from '../types';

const LocationWrapper = styled.div<StyledDiv>`
  display: flex;
  flex-direction: column;
  justify-content: left;
  flex-grow: 1;
  width: 100%;

  ${ErrorWrapper} {
    position: static;
    box-shadow: none;
    transform: none;
  }
  ${ErrorMessage} {
    padding-top: 0;
    min-height: 0;
  }
  ${PrimaryButton} {
    height: 50px;
  }

  ${SecondaryButton} {
    width: 100%;
    max-width: 100px;
  }

  Form {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    margin: 0;
    gap: 10px;
  }
`;

export type CategoryFunnellingLocationConfig = {
  buttonText: string;
  value?: string;
  hideGoBack?: boolean;
};

const ERROR_MESSAGE = 'Please enter a valid postcode';

// eslint-disable-next-line react-hooks/rules-of-hooks
const useGetLocation = useGetLocationHookFactory();

const LocationScreen = ({
  buttonText,
  onComplete,
  onBack,
  value,
  hideGoBack = false,
}: CategoryFunnellingLocationConfig &
  ScreenCommonProps<[location: string]>) => {
  const [searchSubmitted, setSearchSubmitted] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [initialSearchPhrase, setInitialSearchPhrase] = useState('');
  const [postcode, setPostcode] = useState<DropdownOption | undefined>(
    value && isFullPostcode(value) ? { label: value, id: value } : undefined,
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [postcodeOptions, setPostcodeOptions] = useState<DropdownOption[]>([]);
  const { lookupPostcode } = useConsumerActions();
  const onInput = async (inputValue: string) => {
    const sanitizedInputValue = inputValue.trim();
    if (sanitizedInputValue.length < 2) return;

    try {
      const response = await lookupPostcode(sanitizedInputValue);

      const suggestedPostcodes = response.map(option => ({
        id: option,
        label: option,
      }));

      if (response.length > 0) {
        if (response.length === 1) setPostcode(suggestedPostcodes[0]);
        setPostcodeOptions(suggestedPostcodes);
        return;
      }
    } catch (error) {
      // try to process input locally
    }

    // we're in an error state or got zero suggestions, try local validation
    if (isFullPostcode(sanitizedInputValue)) {
      setErrorMessage('');
      setPostcode({ label: sanitizedInputValue, id: sanitizedInputValue });
    } else {
      setErrorMessage(ERROR_MESSAGE);
    }
  };

  const debouncedInput = debounce(onInput, 400);

  const onSelectValue = async (_: string, selectedValue?: DropdownOption) => {
    if (!selectedValue) return;
    setErrorMessage('');
    setPostcode(selectedValue);
    setInitialSearchPhrase(selectedValue.label);
  };

  const onSuccess = (location?: string) => {
    if (!location) {
      if (!postcode) return setErrorMessage(ERROR_MESSAGE);
      return;
    }

    return setPostcode({ label: location, id: location });
  };

  const onGetLocation = useGetLocation({
    onUpdateLoading: setGettingLocation,
    onSuccess,
  });

  const onIconClick = useCallback(() => {
    track('global.geo_location_pin');
    onGetLocation();
  }, [onGetLocation]);

  const onSubmit = () => {
    const location = postcode?.label;
    if (!location) return setErrorMessage(ERROR_MESSAGE);

    setSearchSubmitted(true);

    return onComplete(location);
  };

  const onkeydown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') return onSubmit();
  };

  return (
    <Wrapper as="article">
      <LocationWrapper>
        <Header>What&apos;s your postcode?</Header>
        <Subtitle>
          We use this to make sure the tradesperson operates in your area.
        </Subtitle>
        <div className="flex flex-col gap-2 3xs:flex-row" onKeyDown={onkeydown}>
          <DropdownSearch
            name="postcode"
            onInput={debouncedInput}
            options={postcodeOptions}
            onChange={onSelectValue}
            autofilter={false}
            value={postcode}
            placeholder={
              gettingLocation ? 'Finding...' : 'Postcode (of your project)'
            }
            rightIcon={IconMyLocation}
            errorMessage={errorMessage}
            rightIconProps={{
              color: '#323232',
              size: 'medium',
              className: 'cursor-pointer',
              onClick: onIconClick,
            }}
            initialSearchPhrase={initialSearchPhrase}
            autocomplete={'postal-code'}
          />
          <PrimaryButton className="h-14" onClick={onSubmit}>
            {buttonText}
          </PrimaryButton>
        </div>
      </LocationWrapper>
      {searchSubmitted ? <ProgressLoader /> : null}
      {!hideGoBack && (
        <FunnellingFooter>
          <FunnellingBackButton onClick={onBack} />
        </FunnellingFooter>
      )}
    </Wrapper>
  );
};

export default LocationScreen;
